import React, { useState } from 'react'
import styled from 'styled-components'
import Image from 'next/legacy/image'
import { ModalBackground } from 'components/common/page-modal'
import { Column, Row } from 'components/common/layout'
import { useStartups } from 'apis/hooks'
import { H5Text, MediumText } from 'components/common/text'
import { MyLink } from 'components/common/link'
import { theme } from 'infra/theme'
import { SearchedStartup } from 'types/common'
import { analytics } from 'infra/analytics'
import { BoxInput } from 'components/common/input'

export const highlightSearchQuery = (name: string, searchQuery: string) => {
  const escapedSearchQuery = searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const highlighted = name.replace(
    new RegExp(escapedSearchQuery, 'g'),
    (str) => `<span style="color: ${theme.color.primary}">${str}</span>`,
  )
  return highlighted
}

export const StartupItem: React.FCC<{
  startup: SearchedStartup
  onClick?: () => void
  newTab?: boolean
}> = ({ startup, onClick, newTab, children }) => (
  <MyLink href={`/startups/${startup.id}`} newTab={newTab} onClick={onClick}>
    <StartupItemWrapper>
      {startup.thumbnail && (
        <StartupLogoWrapper>
          <Image
            src={startup.thumbnail}
            width={20}
            height={20}
            alt={startup.thumbnail}
            objectFit='contain'
          />
        </StartupLogoWrapper>
      )}
      <H5Text dangerouslySetInnerHTML={{ __html: startup.name }} />
      {children}
    </StartupItemWrapper>
  </MyLink>
)

const SearchResult: React.FC<{ searchQuery: string; onClose: () => void }> = ({
  searchQuery,
  onClose,
}) => {
  const { data: startups } = useStartups({
    offset: 0,
    limit: 5,
    searchQuery,
    isPublished: true,
  })
  const SEARCHED_STARTUPS_KEY = 'SearchedStartups'
  const [searchedStartups, setSearchedStartups] = useState(
    localStorage.getItem(SEARCHED_STARTUPS_KEY),
  )
  const clickStartupItem = (searchedStartup: SearchedStartup) => {
    const newSearchedStartups = searchedStartups
      ? [
          searchedStartup,
          ...JSON.parse(searchedStartups).filter(
            (el: SearchedStartup) => el.id !== searchedStartup.id,
          ),
        ]
      : [searchedStartup]
    localStorage.setItem(
      SEARCHED_STARTUPS_KEY,
      JSON.stringify(newSearchedStartups),
    )
    setSearchedStartups(JSON.stringify(newSearchedStartups))
  }
  const deleteStartupItem = (item: SearchedStartup) => {
    if (!searchedStartups) return
    const filtered = JSON.parse(searchedStartups).filter(
      (searched: SearchedStartup) => searched.id !== item.id,
    )
    if (filtered.length) {
      localStorage.setItem(SEARCHED_STARTUPS_KEY, JSON.stringify(filtered))
      setSearchedStartups(JSON.stringify(filtered))
    } else {
      localStorage.removeItem(SEARCHED_STARTUPS_KEY)
      setSearchedStartups(null)
    }
  }
  return (
    <>
      {!searchQuery && (
        <Column>
          <Row style={{ alignItems: 'start', gap: 8 }}>
            <MediumText style={{ lineHeight: 1.4 }}>📢</MediumText>
            <MediumText style={{ color: theme.color.black4, lineHeight: 1.4 }}>
              다양한 키워드로 스타트업과 채용 공고를 검색할 수 있게, 곧 업데이트
              예정이에요
            </MediumText>
          </Row>
          <Row style={{ justifyContent: 'space-between', margin: '16px 0' }}>
            <H5Text style={{ fontWeight: theme.fontWeight.semiBold }}>
              최근 검색 스타트업
            </H5Text>
            <MediumText
              style={{ color: theme.color.gray2, cursor: 'pointer' }}
              onClick={() => {
                localStorage.removeItem(SEARCHED_STARTUPS_KEY)
                setSearchedStartups(null)
              }}
            >
              전체 삭제
            </MediumText>
          </Row>
        </Column>
      )}
      <Column>
        {searchQuery ? (
          startups?.items?.length ? (
            startups.items.map((item) => {
              const name = highlightSearchQuery(item.name, searchQuery)
              return (
                <StartupItem
                  key={item.id}
                  startup={{ ...item, name }}
                  onClick={() => {
                    clickStartupItem(item)
                    analytics.track('click_mi_search_startup', {
                      startupId: item.id,
                      startupName: item.name,
                    })
                    onClose()
                  }}
                />
              )
            })
          ) : (
            <PlaceholderContainer>
              <PlaceholderText>일치하는 스타트업이 없어요</PlaceholderText>
            </PlaceholderContainer>
          )
        ) : searchedStartups && JSON.parse(searchedStartups).length ? (
          JSON.parse(searchedStartups)
            .slice(0, 5)
            .map((item: SearchedStartup) => (
              <StartupItem
                key={item.id}
                startup={item}
                onClick={() => {
                  clickStartupItem(item)
                  analytics.track('click_mi_search_startup', {
                    startupId: item.id,
                    startupName: item.name,
                  })
                  onClose()
                }}
              >
                <DeleteIconWrapper
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    deleteStartupItem(item)
                  }}
                >
                  <Image
                    src='/delete-gray.svg'
                    width={24}
                    height={24}
                    alt='delete icon'
                  />
                </DeleteIconWrapper>
              </StartupItem>
            ))
        ) : (
          <PlaceholderContainer>
            <PlaceholderText>최근 검색한 스타트업이 없어요</PlaceholderText>
          </PlaceholderContainer>
        )}
      </Column>
    </>
  )
}

export const SearchStartupInput: React.FC<{
  value: string
  onChange: (v: string) => void
}> = ({ value, onChange }) => {
  return (
    <Row style={{ position: 'relative', marginBottom: 16 }}>
      <Row style={{ position: 'absolute', left: 16 }}>
        <Image
          src='/search-primary.svg'
          width={24}
          height={24}
          alt='search icon'
        />
      </Row>
      <SearchInput
        placeholder='스타트업 이름 검색'
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoFocus
      />
    </Row>
  )
}

export const StartupSearchModal: React.FC<{ onClose: () => void }> = ({
  onClose,
}) => {
  const [input, setInput] = useState('')
  return (
    <ModalBackground onClick={onClose}>
      <TopModalContainer onClick={(e) => e.stopPropagation()}>
        <SearchContent>
          <SearchStartupInput value={input} onChange={(v) => setInput(v)} />
          <React.Suspense>
            <SearchResult searchQuery={input} onClose={onClose} />
          </React.Suspense>
        </SearchContent>
      </TopModalContainer>
    </ModalBackground>
  )
}

const TopModalContainer = styled(Column)`
  position: fixed;
  top: 0;
  width: 100vw;
  align-items: center;
  background-color: ${(p) => p.theme.color.white2};
  @media ${(p) => p.theme.deviceSize.desktop} {
    padding: 56px 0 50px;
  }
  @media ${(p) => p.theme.deviceSize.mobile} {
    padding: 20px;
  }
  animation: slide 0.6s ease;

  @keyframes slide {
    from {
      top: -400px;
    }
    to {
      top: 0;
    }
  }
`

const SearchContent = styled(Column)`
  position: relative;
  width: 1060px;
  @media ${(p) => p.theme.deviceSize.mobile} {
    width: 100%;
  }
`

const SearchInput = styled(BoxInput)`
  width: 100%;
  padding-left: 56px;
  border-color: ${(p) => p.theme.color.primary};
  border-radius: 16px;
  font-size: ${(p) => p.theme.fontSize.h5};
  :focus {
    border-color: ${(p) => p.theme.color.primary};
  }
`

export const StartupItemWrapper = styled(Row)`
  gap: 10px;
  padding: 20px;
  position: relative;
  border-radius: 4px;
  :hover {
    background-color: ${(p) => p.theme.color.gray5}26;
  }
`

const StartupLogoWrapper = styled(Row)`
  border-radius: 8px;
  overflow: hidden;
`

const DeleteIconWrapper = styled(Row)`
  position: absolute;
  right: 20px;
`

const PlaceholderText = styled(H5Text)`
  color: ${(p) => p.theme.color.gray2};
`

const PlaceholderContainer = styled(Row)`
  justify-content: center;
  margin: 16px 0;
`
